import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FaSpinner } from "react-icons/fa";
import useStore from "../../../../store/useStore";
import StatusPill from "./StatusPill";
import { Field, SelectField, inputClass } from "./FormFields";
import {
  PAUSE_REASONS,
  PLAN_TYPES,
  formatDate,
  formatDeliverySlot,
  formatMeasure,
  formatSubscriptionFrequency,
  formatSubscriptionPayment,
  formatVariantSize,
  labelFrom,
  price,
  tomorrowIso,
} from "./subscriptionUtils";

const panelClass = "rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6";
const buttonClass = "rounded-xl px-4 py-2.5 text-sm font-bold transition disabled:opacity-50";

const API_RECORD_GROUPS = [
  {
    title: "Subscription",
    fields: [
      ["subscription_id", "Subscription ID"], ["customer_id", "Customer ID"],
      ["product_id", "Product ID"], ["product_variant_id", "Variant ID"],
      ["address_id", "Address ID"], ["status", "Status"],
      ["quantity", "Quantity"], ["quantity_unit", "Quantity unit"],
      ["frequency", "Delivery frequency"], ["plan_type", "Plan"],
      ["delivery_slot", "Delivery slot"], ["morning_slot", "Morning slot"],
      ["morning_quantity", "Morning quantity"], ["evening_slot", "Evening slot"],
      ["evening_quantity", "Evening quantity"], ["payment_method", "Payment method"],
    ],
  },
  {
    title: "Billing and deliveries",
    fields: [
      ["cf_plan_id", "Cashfree plan ID"], ["cf_subscription_id", "Cashfree subscription ID"],
      ["cf_mandate_id", "UPI mandate ID"], ["cf_subscription_status", "AutoPay status"],
      ["modify_otp", "Modification OTP state"], ["modify_otp_expires_at", "OTP expiry"],
      ["unit_price", "Unit price"], ["total_plan_amount", "Plan amount"],
      ["start_date", "Start date"], ["end_date", "End date"],
      ["next_billing_date", "Next billing date"], ["next_delivery", "Next delivery"],
      ["delivered_count", "Delivered count"], ["pending_count", "Pending count"],
    ],
  },
  {
    title: "Product and delivery address",
    fields: [
      ["product_name", "Product"], ["variant_name", "Variant"], ["unit_name", "Variant unit"],
      ["image1", "Product image 1"], ["image2", "Product image 2"],
      ["address", "Address"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"],
      ["store_id", "Store ID"], ["hub_id", "Hub ID"], ["delivery_zone_id", "Delivery zone ID"],
      ["pause_history", "Pause history"], ["created_at", "Created"], ["updated_at", "Last updated"],
    ],
  },
];

const recordValue = (subscription, field) => {
  const value = subscription[field];
  if (value === null || value === undefined || value === "") return "—";
  if (field === "frequency") return formatSubscriptionFrequency(value);
  if (field === "plan_type") return labelFrom(PLAN_TYPES, value);
  if (field === "payment_method") return formatSubscriptionPayment(value);
  if (["delivery_slot", "morning_slot", "evening_slot"].includes(field)) return formatDeliverySlot(value);
  if (["quantity", "morning_quantity", "evening_quantity"].includes(field)) {
    return formatMeasure(value, subscription.quantity_unit || subscription.unit_name);
  }
  if (["unit_price", "total_plan_amount"].includes(field)) return price(value);
  if (["start_date", "end_date", "next_billing_date", "next_delivery"].includes(field)) return formatDate(value);
  if (["created_at", "updated_at"].includes(field)) {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("en-IN");
  }
  if (field === "modify_otp") return "OTP is set";
  if (field === "modify_otp_expires_at") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("en-IN");
  }
  if (field === "cf_subscription_status") {
    return String(value).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
  if (field === "pause_history") {
    return Array.isArray(value) && value.length ? JSON.stringify(value) : "No pause history";
  }
  return String(value);
};

const SubscriptionApiRecord = ({ subscription }) => (
  <section className={`${panelClass} space-y-5`}>
    <div>
      <h2 className="text-lg font-extrabold text-gray-900">Subscription information</h2>
      <p className="mt-1 text-xs text-gray-500">Fields returned for this subscription by the API.</p>
    </div>
    {API_RECORD_GROUPS.map((group) => (
      <div key={group.title}>
        <h3 className="mb-2 text-xs font-extrabold uppercase tracking-wide text-primary-700">{group.title}</h3>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {group.fields.map(([field, label]) => (
            <div key={field} className="min-w-0 rounded-xl bg-gray-50 px-3 py-2.5">
              <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</p>
              {field === "image1" || field === "image2" ? (
                subscription[field] ? (
                  <img src={subscription[field]} alt={label} className="mt-1 h-14 w-14 rounded-lg bg-white object-contain p-1" />
                ) : <p className="mt-1 text-sm font-bold text-gray-700">—</p>
              ) : (
                <p className="mt-1 break-words text-sm font-bold text-gray-800">{recordValue(subscription, field)}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    ))}
  </section>
);

const SubscriptionRoutePage = () => {
  const { subscriptionId, action = "view" } = useParams();
  const navigate = useNavigate();
  const {
    subscription_detail: subscription,
    subscription_detail_loading: detailLoading,
    subscription_deliveries: deliveryPage,
    subscription_deliveries_loading: deliveriesLoading,
    subscription_action_loading: actionLoading,
    subscriptions_error: detailError,
    subscription_config: config,
    fetchSubscriptionDetail,
    fetchSubscriptionDeliveries,
    fetchSubscriptionConfig,
    sendSubscriptionModifyOtp,
    changeSubscriptionQuantity,
    changeSubscriptionPlan,
    pauseSubscription,
  } = useStore();
  const [activeTab, setActiveTab] = useState("details");
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [form, setForm] = useState(null);
  const [formSubscriptionId, setFormSubscriptionId] = useState(null);
  const [pauseForm, setPauseForm] = useState({
    pause_from: tomorrowIso(),
    pause_to: tomorrowIso(),
    reason: "temporary_pause",
    comments: "",
  });

  useEffect(() => {
    fetchSubscriptionDetail(subscriptionId).catch((error) =>
      toast.error(error.message || "Could not load this subscription"),
    );
    fetchSubscriptionConfig();
  }, [fetchSubscriptionDetail, fetchSubscriptionConfig, subscriptionId]);

  useEffect(() => {
    if (!subscription || String(subscription.subscription_id) !== String(subscriptionId)) return;
    if (String(formSubscriptionId) === String(subscriptionId)) return;
    setForm({
      quantity: Math.max(1, Math.round(Number(subscription.quantity || 1))),
      plan_type: subscription.plan_type || "monthly",
      otp: "",
    });
    setFormSubscriptionId(subscriptionId);
  }, [subscription, subscriptionId, formSubscriptionId]);

  useEffect(() => {
    if (activeTab === "deliveries") {
      fetchSubscriptionDeliveries(subscriptionId, { page: 1, limit: 20 }).catch((error) =>
        toast.error(error.message || "Could not load delivery history"),
      );
    }
  }, [activeTab, fetchSubscriptionDeliveries, subscriptionId]);

  const plans = useMemo(() => {
    const rows = (config?.plans || []).map((item) => ({
      value: String(item.plan_key || item.value || "monthly").toLowerCase(),
      label: item.plan_name || item.label || item.plan_key || "Monthly",
    }));
    return rows.length ? rows : PLAN_TYPES;
  }, [config?.plans]);
  const goToList = () => navigate("/account/subscriptions");
  const updateForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  const handleSendOtp = async () => {
    setOtpSending(true);
    setOtpRequested(false);
    updateForm("otp", "");
    try {
      const result = await sendSubscriptionModifyOtp(subscriptionId);
      setOtpRequested(true);
      toast.success(result?.message || "Verification code sent to your registered mobile");
    } catch (error) {
      toast.error(error.message || "Could not send verification code");
    } finally {
      setOtpSending(false);
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!otpRequested || !form?.otp?.trim()) {
      toast.error("Request and enter the verification OTP first");
      return;
    }
    const quantityChanged = Number(form.quantity) !== Number(subscription.quantity);
    const planChanged = form.plan_type !== subscription.plan_type;
    if (!quantityChanged && !planChanged) {
      toast("There are no changes to save");
      return;
    }
    try {
      if (quantityChanged) {
        await changeSubscriptionQuantity(subscriptionId, {
          quantity: Math.max(1, Math.round(Number(form.quantity || 1))),
          otp: form.otp,
        });
      }
      if (planChanged) {
        await changeSubscriptionPlan(subscriptionId, {
          plan_type: form.plan_type,
          otp: form.otp,
        });
      }
      const refreshedSubscription = await fetchSubscriptionDetail(subscriptionId);
      setForm({
        quantity: Math.max(1, Math.round(Number(refreshedSubscription.quantity || 1))),
        plan_type: refreshedSubscription.plan_type || "monthly",
        otp: "",
      });
      setOtpRequested(false);
      toast.success("Subscription updated");
    } catch (error) {
      toast.error(error.message || "Could not update subscription");
    }
  };

  const handlePauseSubmit = async (event) => {
    event.preventDefault();
    if (pauseForm.pause_to < pauseForm.pause_from) {
      toast.error("Pause end date must be on or after the start date");
      return;
    }
    try {
      const response = await pauseSubscription(subscriptionId, pauseForm);
      toast.success(response?.message || "Subscription paused");
      goToList();
    } catch (error) {
      toast.error(error.message || "Could not pause subscription");
    }
  };

  const changePage = (page) =>
    fetchSubscriptionDeliveries(subscriptionId, { page, limit: deliveryPage.limit || 20 }).catch((error) =>
      toast.error(error.message || "Could not load delivery history"),
    );

  const isCorrectSubscription = subscription && String(subscription.subscription_id) === String(subscriptionId);
  const loading = detailLoading || (!isCorrectSubscription && !detailError);
  const pageTitle = action === "edit" ? "Edit subscription" : action === "pause" ? "Pause subscription" : "Subscription details";
  const address = subscription?.address;
  const addressText = [
    ...(typeof address === "string" ? [address] : [address?.address_line, address?.address]),
    subscription?.city,
    subscription?.state,
    subscription?.pincode,
  ].filter(Boolean).join(", ");

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 py-2 sm:py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/account/subscriptions" className="text-sm font-bold text-primary-700 hover:text-primary-800">← My subscriptions</Link>
          <h1 className="mt-2 text-2xl font-extrabold text-gray-900">{pageTitle}</h1>
          {isCorrectSubscription && <p className="mt-1 text-sm text-gray-500">{subscription.product_name} · {subscription.subscription_code || subscription.subscription_number || `#${subscription.subscription_id}`}</p>}
        </div>
        {isCorrectSubscription && <StatusPill status={subscription.status} />}
      </div>

      {loading ? (
        <div className={`${panelClass} flex min-h-48 items-center justify-center`}><FaSpinner className="h-6 w-6 animate-spin text-primary-600" /></div>
      ) : !isCorrectSubscription ? (
        <div className={`${panelClass} text-center`}>
          <p className="text-sm font-semibold text-red-600">{detailError || "This subscription could not be found."}</p>
          <button type="button" onClick={goToList} className={`${buttonClass} mt-4 bg-primary-600 text-white`}>Back to subscriptions</button>
        </div>
      ) : (
        <>
          {action === "view" && (
            <div className="flex flex-wrap gap-2">
              <button className={`${buttonClass} bg-primary-600 text-white hover:bg-primary-700`} onClick={() => navigate(`/account/subscriptions/${subscriptionId}/edit`)}>Edit subscription</button>
              {String(subscription.status).toLowerCase() === "active" && <button className={`${buttonClass} bg-amber-100 text-amber-800 hover:bg-amber-200`} onClick={() => navigate(`/account/subscriptions/${subscriptionId}/pause`)}>Pause</button>}
              {String(subscription.status).toLowerCase() === "paused" && <button className={`${buttonClass} bg-primary-50 text-primary-700`} onClick={() => navigate(`/account/subscriptions/${subscriptionId}/edit`)}>Edit subscription</button>}
            </div>
          )}

          {action === "edit" && form && (
            <div className="space-y-5">
              <SubscriptionApiRecord subscription={subscription} />
              <form onSubmit={handleEditSubmit} className={`${panelClass} space-y-5`}>
                <div>
                  <h2 className="text-lg font-extrabold text-gray-900">Edit quantity or plan</h2>
                  <p className="mt-1 text-xs text-gray-500">The form is limited to fields supported by the subscription edit API.</p>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                <Field label={`Quantity (${subscription.quantity_unit || subscription.unit_name || "unit"})`}>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => updateForm("quantity", Math.max(1, Number(form.quantity || 1) - 1))}
                      disabled={Number(form.quantity) <= 1}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-base font-bold text-gray-700 transition hover:bg-gray-100 disabled:opacity-40"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      required
                      value={form.quantity}
                      onChange={(event) => {
                        const val = event.target.value;
                        if (val === "") {
                          updateForm("quantity", "");
                          return;
                        }
                        const parsed = parseInt(val, 10);
                        updateForm("quantity", isNaN(parsed) ? 1 : Math.max(1, parsed));
                      }}
                      onBlur={() => {
                        if (!form.quantity || Number(form.quantity) < 1) {
                          updateForm("quantity", 1);
                        }
                      }}
                      className={`${inputClass} text-center font-bold`}
                    />
                    <button
                      type="button"
                      onClick={() => updateForm("quantity", Number(form.quantity || 1) + 1)}
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-base font-bold text-gray-700 transition hover:bg-gray-100"
                    >
                      +
                    </button>
                  </div>
                </Field>
                <Field label="Plan"><SelectField value={form.plan_type} onChange={(event) => updateForm("plan_type", event.target.value)}>{plans.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</SelectField></Field>
                </div>
                <div className="rounded-xl border border-primary-100 bg-primary-50 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div><p className="text-sm font-extrabold text-gray-900">Verify your changes</p><p className="mt-1 text-xs text-gray-600">An OTP is required by the subscription edit API.</p></div>
                    <button type="button" onClick={handleSendOtp} disabled={otpSending || actionLoading} className={`${buttonClass} bg-white text-primary-700`}>{otpSending ? "Sending…" : otpRequested ? "Resend OTP" : "Send OTP"}</button>
                  </div>
                  <input className={`${inputClass} mt-3 max-w-sm`} inputMode="numeric" autoComplete="one-time-code" maxLength={8} placeholder="Enter OTP" value={form.otp} onChange={(event) => updateForm("otp", event.target.value.replace(/\D/g, ""))} required />
                </div>
                <div className="flex flex-wrap gap-2"><button type="button" onClick={() => navigate(`/account/subscriptions/${subscriptionId}/view`)} className={`${buttonClass} border border-gray-200 text-gray-700`}>Back</button><button type="submit" disabled={actionLoading} className={`${buttonClass} bg-primary-600 text-white hover:bg-primary-700`}>{actionLoading ? "Saving…" : "Save changes"}</button></div>
              </form>
            </div>
          )}

          {action === "pause" && (
            <form onSubmit={handlePauseSubmit} className={`${panelClass} space-y-5`}>
              <p className="text-sm text-gray-600">Choose the dates when deliveries should be paused. Your subscription details will refresh from the API after the request completes.</p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Pause from"><input type="date" min={tomorrowIso()} value={pauseForm.pause_from} onChange={(event) => setPauseForm((current) => ({ ...current, pause_from: event.target.value, pause_to: current.pause_to < event.target.value ? event.target.value : current.pause_to }))} className={inputClass} required /></Field>
                <Field label="Pause to"><input type="date" min={pauseForm.pause_from || tomorrowIso()} value={pauseForm.pause_to} onChange={(event) => setPauseForm((current) => ({ ...current, pause_to: event.target.value }))} className={inputClass} required /></Field>
                <Field label="Reason"><SelectField value={pauseForm.reason} onChange={(event) => setPauseForm((current) => ({ ...current, reason: event.target.value }))}>{PAUSE_REASONS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</SelectField></Field>
                <Field label="Comments"><textarea rows={3} maxLength={200} value={pauseForm.comments} onChange={(event) => setPauseForm((current) => ({ ...current, comments: event.target.value }))} className={inputClass} placeholder="Optional note" /></Field>
              </div>
              <div className="flex flex-wrap gap-2"><button type="button" onClick={() => navigate(`/account/subscriptions/${subscriptionId}/view`)} className={`${buttonClass} border border-gray-200 text-gray-700`}>Back</button><button type="submit" disabled={actionLoading} className={`${buttonClass} bg-amber-500 text-white hover:bg-amber-600`}>{actionLoading ? "Pausing…" : "Confirm pause"}</button></div>
            </form>
          )}

          {action === "view" && (
            <>
              <div className={panelClass}>
                <div className="mb-4 flex flex-wrap gap-2">
                  {[{ key: "details", label: "Details" }, { key: "deliveries", label: "Delivery history" }].map((tab) => <button type="button" key={tab.key} onClick={() => setActiveTab(tab.key)} className={`${buttonClass} ${activeTab === tab.key ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>{tab.label}</button>)}
                </div>
                {activeTab === "details" ? (
                  <div className="space-y-5">
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {[
                        ["Product", subscription.product_name], ["Variant", formatVariantSize(subscription)],
                        [String(subscription.frequency).toLowerCase() === "twice_a_day" ? "Daily total" : "Quantity per delivery", formatMeasure(subscription.quantity, subscription.quantity_unit || subscription.unit_name)],
                        ["Frequency", formatSubscriptionFrequency(subscription.frequency)], ["Plan", labelFrom(plans, subscription.plan_type)],
                        ["Delivery slot", formatDeliverySlot(subscription.delivery_slot)], ["Payment method", formatSubscriptionPayment(subscription.payment_method)],
                        ["Morning delivery", `${formatMeasure(subscription.morning_quantity, subscription.quantity_unit || subscription.unit_name)} · ${formatDeliverySlot(subscription.morning_slot)}`],
                        ...(subscription.evening_slot ? [["Evening delivery", `${formatMeasure(subscription.evening_quantity, subscription.quantity_unit || subscription.unit_name)} · ${formatDeliverySlot(subscription.evening_slot)}`]] : []),
                        ["Start date", formatDate(subscription.start_date)], ["End date", formatDate(subscription.end_date)], ["Created", formatDate(subscription.created_at)],
                        ["Next delivery", subscription.next_delivery ? formatDate(subscription.next_delivery) : String(subscription.status).toLowerCase() === "pending_mandate" ? "Awaiting AutoPay" : "Not scheduled"], ["Unit price", price(subscription.unit_price)],
                        ["Next billing", formatDate(subscription.next_billing_date)],
                        ...(subscription.cf_subscription_status ? [["AutoPay status", String(subscription.cf_subscription_status).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase())]] : []),
                        ...(subscription.cf_subscription_id ? [["Cashfree subscription", subscription.cf_subscription_id]] : []),
                        ...(subscription.cf_mandate_id ? [["UPI mandate reference", subscription.cf_mandate_id]] : []),
                        ["Plan amount", price(subscription.total_plan_amount)], ["Delivered", subscription.delivered_count ?? 0], ["Pending", subscription.pending_count ?? 0],
                      ].map(([label, value]) => <div key={label} className="rounded-xl bg-gray-50 px-4 py-3"><p className="text-xs font-bold uppercase tracking-wide text-gray-400">{label}</p><p className="mt-1 break-words text-sm font-extrabold text-gray-900">{value !== null && value !== undefined && value !== "" ? value : "Not set"}</p></div>)}
                    </div>
                    {(addressText || subscription.delivery_address || subscription.city || subscription.pincode || subscription.name || subscription.customer_name || subscription.mobile || subscription.phone) && (
                      <div className="rounded-xl bg-primary-50 px-4 py-3">
                        <p className="text-xs font-bold uppercase tracking-wide text-primary-700">Delivery address</p>
                        {(subscription.name || subscription.customer_name || subscription.recipient_name || subscription.mobile || subscription.phone || subscription.customer_mobile || address?.name || address?.mobile) && (
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 text-sm font-bold text-gray-900">
                            <span>{subscription.name || subscription.customer_name || subscription.recipient_name || address?.name}</span>
                            {(subscription.mobile || subscription.phone || subscription.customer_mobile || address?.mobile) && (
                              <span className="font-semibold text-gray-600">
                                • {subscription.mobile || subscription.phone || subscription.customer_mobile || address?.mobile}
                              </span>
                            )}
                          </div>
                        )}
                        <p className="mt-0.5 text-sm font-semibold text-gray-800">
                          {addressText || subscription.delivery_address || [subscription.city, subscription.state, subscription.pincode].filter(Boolean).join(", ")}
                        </p>
                      </div>
                    )}
                    {subscription.active_pause && <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3"><p className="text-sm font-extrabold text-amber-900">Paused {formatDate(subscription.active_pause.pause_from)} – {formatDate(subscription.active_pause.pause_to)}</p><p className="text-xs text-amber-800">{String(subscription.active_pause.reason || "").replace(/_/g, " ")} {subscription.active_pause.comments}</p></div>}
                    {subscription.pause_history?.length > 0 && <div><h2 className="mb-2 text-sm font-extrabold text-gray-900">Pause history</h2><div className="space-y-2">{subscription.pause_history.map((pause, index) => <div key={pause.pause_id || `${pause.pause_from}-${index}`} className="rounded-xl border border-gray-100 px-4 py-3 text-sm"><span className="font-bold">{formatDate(pause.pause_from)} – {formatDate(pause.pause_to)}</span><span className="ml-2 text-gray-500">{String(pause.reason || "Pause").replace(/_/g, " ")}</span></div>)}</div></div>}
                  </div>
                ) : (
                  <div>
                    {deliveriesLoading && <div className="flex justify-center py-10"><FaSpinner className="h-5 w-5 animate-spin text-primary-600" /></div>}
                    {!deliveriesLoading && !deliveryPage.deliveries?.length && <p className="py-10 text-center text-sm text-gray-500">No delivery history found.</p>}
                    {!deliveriesLoading && deliveryPage.deliveries?.length > 0 && <div className="space-y-2">{deliveryPage.deliveries.map((delivery, index) => <div key={delivery.delivery_id || delivery.id || index} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gray-50 px-4 py-3"><div><p className="text-sm font-bold text-gray-900">{formatDate(delivery.delivery_date || delivery.scheduled_date || delivery.date)}</p><p className="text-xs text-gray-500">{delivery.delivery_slot || delivery.slot || "Scheduled delivery"}</p></div><span className="rounded-full bg-white px-3 py-1 text-xs font-bold capitalize text-gray-700">{String(delivery.status || "scheduled").replace(/_/g, " ")}</span></div>)}</div>}
                    {deliveryPage.total > deliveryPage.limit && <div className="mt-4 flex items-center justify-between"><button type="button" disabled={deliveryPage.page <= 1 || deliveriesLoading} onClick={() => changePage(deliveryPage.page - 1)} className={`${buttonClass} bg-gray-100 text-gray-700`}>Previous</button><span className="text-xs font-bold text-gray-500">Page {deliveryPage.page}</span><button type="button" disabled={deliveryPage.page * deliveryPage.limit >= deliveryPage.total || deliveriesLoading} onClick={() => changePage(deliveryPage.page + 1)} className={`${buttonClass} bg-gray-100 text-gray-700`}>Next</button></div>}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SubscriptionRoutePage;
